/**
 * RolePermissionsAdmin — /admin/rollen (#1049)
 * Rollen × Module-Permissions Matrix mit Schnell-Templates pro Kategorie.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ToggleSwitch } from '../../components/ui';
import { ChevronDown, ChevronRight, Shield, Save, Zap } from 'lucide-react';

const API = '';
const PERMISSIONS = ['none', 'read', 'team', 'full'];
const TEMPLATES = [
  { key: 'voll', perm: 'full', color: 'emerald' },
  { key: 'team', perm: 'team', color: 'blue' },
  { key: 'read', perm: 'read', color: 'slate' },
  { key: 'aus', perm: 'none', color: 'gray' },
];

export default function RolePermissionsAdmin() {
  const { t, language } = useLanguage();
  const lang = language || 'de';
  const [roles, setRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [kategorien, setKategorien] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [collapsedKats, setCollapsedKats] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/admin/roles`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setRoles(d.roles || []);
        if (!activeRole && d.roles?.length) setActiveRole(d.roles[0].rolle);
      })
      .catch(() => setToast({ type: 'error', msg: 'load_failed' }));
  }, []);

  useEffect(() => {
    if (!activeRole) return;
    fetch(`${API}/api/admin/roles/${encodeURIComponent(activeRole)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setMeta(d.meta);
        setKategorien(d.kategorien || []);
        setPermissions(d.permissions || []);
        setDirty(false);
        const allCollapsed = {};
        (d.kategorien || []).forEach(k => { allCollapsed[k.key] = true; });
        setCollapsedKats(allCollapsed);
      });
  }, [activeRole]);

  const grouped = useMemo(() => {
    const map = {};
    for (const p of permissions) {
      if (!map[p.kategorie]) map[p.kategorie] = [];
      map[p.kategorie].push(p);
    }
    return map;
  }, [permissions]);

  function setPerm(modulKey, value) {
    setPermissions(prev => prev.map(p => p.modul_key === modulKey ? { ...p, permission: value } : p));
    setDirty(true);
  }

  async function applyTemplate(kategorie, perm) {
    if (!activeRole || !meta?.editable) return;
    setSaving(true);
    try {
      const tplKey = TEMPLATES.find(t => t.perm === perm)?.key;
      const res = await fetch(`${API}/api/admin/roles/${encodeURIComponent(activeRole)}/template`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kategorie, template: tplKey }),
      });
      if (!res.ok) throw new Error('template_failed');
      const d = await res.json();
      setPermissions(prev => prev.map(p => p.kategorie === kategorie ? { ...p, permission: perm } : p));
      setDirty(false);
      setToast({ type: 'success', msg: `${t('rolesAdmin.templateApplied')}: ${d.applied_to} ${t('rolesAdmin.modules')}` });
      setTimeout(() => setToast(null), 3500);
    } catch {
      setToast({ type: 'error', msg: t('rolesAdmin.saveFailed') });
    } finally { setSaving(false); }
  }

  async function saveAll() {
    if (!activeRole || !dirty) return;
    setSaving(true);
    try {
      const payload = permissions.map(p => ({ modul: p.modul_key, permission: p.permission }));
      const res = await fetch(`${API}/api/admin/roles/${encodeURIComponent(activeRole)}/permissions`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: payload }),
      });
      if (!res.ok) throw new Error('save_failed');
      const d = await res.json();
      setDirty(false);
      setToast({ type: 'success', msg: `${t('common.saved')} (${d.updated})` });
      setTimeout(() => setToast(null), 3500);
    } catch {
      setToast({ type: 'error', msg: t('rolesAdmin.saveFailed') });
    } finally { setSaving(false); }
  }

  function toggleKat(key) {
    setCollapsedKats(prev => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <div className="p-6 w-full max-w-screen-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[var(--primary,#338edc)]/10 flex items-center justify-center">
          <Shield size={20} className="text-[var(--primary,#338edc)]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('rolesAdmin.title')}</h1>
          <p className="text-sm text-[var(--text-tertiary)]">{t('rolesAdmin.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-[260px,1fr] gap-6">
        {/* Sidebar: Rollen-Liste */}
        <div className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card,#fff)] p-2 h-fit sticky top-4">
          <div className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider px-2 py-2">
            {t('rolesAdmin.roles')} ({roles.length})
          </div>
          {roles.map(r => (
            <button
              key={r.rolle}
              onClick={() => setActiveRole(r.rolle)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left text-xs transition-colors ${
                activeRole === r.rolle
                  ? 'bg-[var(--primary,#338edc)]/10 text-[var(--primary,#338edc)] font-semibold'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]'
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                {r.color && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color }} />}
                <span className="truncate">{lang === 'de' ? r.label_de : (r.label_en || r.label_de)}</span>
                {r.is_admin && <span className="text-[9px] px-1 rounded bg-amber-500/10 text-amber-600">A</span>}
              </span>
              <span className="text-[10px] text-[var(--text-tertiary)] flex-shrink-0">
                {Number(r.user_count_legacy) + Number(r.user_count_multi)}
              </span>
            </button>
          ))}
        </div>

        {/* Detail-Panel */}
        <div>
          {!meta ? (
            <div className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card,#fff)] p-8 text-center text-sm text-[var(--text-tertiary)]">
              {t('rolesAdmin.selectRole')}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">{lang === 'de' ? meta.label_de : (meta.label_en || meta.label_de)}</h2>
                  <p className="text-xs text-[var(--text-tertiary)] font-mono">{meta.rolle}</p>
                </div>
                <div className="flex items-center gap-3">
                  {dirty && <span className="text-xs text-amber-600 font-medium">{t('common.unsavedChanges')}</span>}
                  <button
                    onClick={saveAll}
                    disabled={!dirty || saving || !meta.editable}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--primary,#338edc)] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--primary,#338edc)]/90"
                  >
                    <Save size={14} />
                    {saving ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </div>

              {!meta.editable && (
                <div className="mb-4 p-3 rounded-lg bg-amber-500/10 text-amber-700 text-xs border border-amber-500/20">
                  {t('rolesAdmin.notEditable')}
                </div>
              )}

              {kategorien.map(kat => {
                const items = grouped[kat.key] || [];
                if (!items.length) return null;
                const isCollapsed = collapsedKats[kat.key];
                return (
                  <div key={kat.key} className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card,#fff)] mb-4 overflow-hidden">
                    <button
                      onClick={() => toggleKat(kat.key)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-inset)] transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        <span className="text-sm font-semibold text-[var(--text-primary)]">{lang === 'de' ? kat.label_de : kat.label_en}</span>
                        <span className="text-[10px] text-[var(--text-tertiary)]">({items.length})</span>
                      </div>
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <Zap size={12} className="text-[var(--text-tertiary)]" />
                        {TEMPLATES.map(tpl => (
                          <button
                            key={tpl.key}
                            onClick={(e) => { e.stopPropagation(); applyTemplate(kat.key, tpl.perm); }}
                            disabled={saving || !meta.editable}
                            className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors disabled:opacity-50 bg-${tpl.color}-500/10 text-${tpl.color}-700 hover:bg-${tpl.color}-500/20`}
                            title={t(`rolesAdmin.tpl_${tpl.key}`)}
                          >
                            {t(`rolesAdmin.tpl_${tpl.key}`)}
                          </button>
                        ))}
                      </div>
                    </button>
                    {!isCollapsed && (
                      <div className="border-t border-[var(--border-card)] divide-y divide-[var(--border-card)]/50">
                        {items.map(p => (
                          <div key={p.modul_key} className="grid grid-cols-[1fr,auto] gap-3 items-center px-4 py-2.5">
                            <div>
                              <div className="text-xs font-medium text-[var(--text-primary)]">{lang === 'de' ? p.label_de : (p.label_en || p.label_de)}</div>
                              <div className="text-[10px] text-[var(--text-tertiary)] font-mono">{p.modul_key}</div>
                            </div>
                            <div className="flex gap-1">
                              {PERMISSIONS.map(perm => (
                                <button
                                  key={perm}
                                  onClick={() => meta.editable && setPerm(p.modul_key, perm)}
                                  disabled={!meta.editable}
                                  className={`text-[10px] px-2.5 py-1 rounded font-medium transition-colors disabled:cursor-not-allowed ${
                                    p.permission === perm
                                      ? perm === 'full' ? 'bg-emerald-500 text-white'
                                        : perm === 'team' ? 'bg-blue-500 text-white'
                                        : perm === 'read' ? 'bg-slate-500 text-white'
                                        : 'bg-gray-400 text-white'
                                      : 'bg-[var(--bg-inset)] text-[var(--text-secondary)] hover:bg-[var(--border-card)]'
                                  }`}
                                >
                                  {t(`rolesAdmin.perm_${perm}`)}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${
          toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
